import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { orgHeader } from '../tenant';
import styles from './Login.module.css';

const API = process.env.REACT_APP_API_URL || '';

const ROLE_REDIRECT = {
  owner: '/admin/owner',
  admin: '/admin/panel',
  socio: '/admin/socio',
  rrpp: '/admin/rrpp',
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Recuperación de contraseña: mode 'login' | 'forgot' (paso email) | 'reset' (paso código+nueva).
  const [mode, setMode] = useState('login');
  const [code, setCode] = useState('');
  const [newPass, setNewPass] = useState('');
  const [msg, setMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await login(email, password);
      navigate(ROLE_REDIRECT[user.role] || '/admin/owner', { replace: true });
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  async function sendCode(e) {
    e.preventDefault();
    setError(''); setMsg(''); setLoading(true);
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...orgHeader() },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setMode('reset');
      setMsg('Si el email tiene una cuenta, te enviamos un código. Revisá tu correo.');
    } catch { setError('Error de conexión'); } finally { setLoading(false); }
  }

  async function doReset(e) {
    e.preventDefault();
    setError(''); setMsg(''); setLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...orgHeader() },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim(), password: newPass }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'No se pudo restablecer'); return; }
      setMode('login'); setPassword(''); setCode(''); setNewPass('');
      setMsg('✓ Contraseña cambiada. Ingresá con la nueva.');
    } catch { setError('Error de conexión'); } finally { setLoading(false); }
  }

  const backToLogin = () => { setMode('login'); setError(''); setMsg(''); setCode(''); setNewPass(''); };

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logo}>T</div>
          <div className={styles.brandName}>TUKIPRO</div>
          <div className={styles.brandSub}>Panel de administración</div>
        </div>

        {mode === 'login' && (
          <>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>Email</label>
                <input className={styles.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tuemail@tudominio.com" required autoFocus />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Contraseña</label>
                <input className={styles.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              {msg && <div style={{ color: '#2ecc71', fontSize: 13, marginBottom: 8 }}>{msg}</div>}
              {error && <div className={styles.error}>{error}</div>}
              <button className={styles.btn} type="submit" disabled={loading}>{loading ? 'Ingresando...' : 'INGRESAR'}</button>
            </form>
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button type="button" onClick={() => { setMode('forgot'); setError(''); setMsg(''); }} style={{ background: 'transparent', border: 0, color: 'var(--ink-700)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>¿Olvidaste tu contraseña?</button>
            </div>
          </>
        )}

        {mode === 'forgot' && (
          <form onSubmit={sendCode} className={styles.form}>
            <div style={{ color: 'var(--ink-700)', fontSize: 13.5, lineHeight: 1.5, marginBottom: 14 }}>Ingresá tu email y te enviamos un código para elegir una nueva contraseña.</div>
            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <input className={styles.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tuemail@tudominio.com" required autoFocus />
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <button className={styles.btn} type="submit" disabled={loading}>{loading ? 'Enviando…' : 'Enviar código'}</button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button type="button" onClick={backToLogin} style={{ background: 'transparent', border: 0, color: 'var(--ink-700)', fontSize: 13, cursor: 'pointer' }}>← Volver</button>
            </div>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={doReset} className={styles.form}>
            {msg && <div style={{ color: 'var(--ink-700)', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>{msg}</div>}
            <div className={styles.field}>
              <label className={styles.label}>Código (6 dígitos)</label>
              <input className={styles.input} inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="● ● ● ● ● ●" required autoFocus />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Nueva contraseña</label>
              <input className={styles.input} type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Mínimo 6 caracteres" autoComplete="new-password" required />
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <button className={styles.btn} type="submit" disabled={loading || code.length !== 6}>{loading ? 'Cambiando…' : 'Cambiar contraseña'}</button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button type="button" onClick={backToLogin} style={{ background: 'transparent', border: 0, color: 'var(--ink-700)', fontSize: 13, cursor: 'pointer' }}>← Volver</button>
            </div>
          </form>
        )}

        <div className={styles.footer}>
          <a href="/tpv" className={styles.tpvLink}>Acceso TPV (PIN de caja) →</a>
        </div>
      </div>
    </div>
  );
}
